import { useDispatch, useSelector } from 'react-redux'
import PropTypes from 'prop-types'
import React, { useCallback, useEffect, useState } from 'react'

import { I18N } from 'constants/props'
import { RESTORE_OUTCOME, restoreSession } from 'app_modules/session/actions/restoreSession'
import * as sessionSelectors from 'app_modules/session/selectors'
import Translation from 'generics/Translation'

import ApplicationBootstrapFallback from './ApplicationBootstrapFallback'
import styles from './SessionRestore.scss'

// What SessionRestore is showing while a reloaded session is rebuilt.
const STATUS = Object.freeze({
  // GET /sessions/current is in flight: show the bootstrap loader.
  RESTORING: 'restoring',
  // A transient failure (connection, server error, rate limit): offer to try again.
  FAILED: 'failed',
  // Restored, logged out, or nothing to restore: render the routes.
  DONE: 'done',
})

/**
 * Holds the routes back while a logged-in user's session is rebuilt after a full page
 * reload (SIM-388). Profiles and account data are no longer persisted in localStorage, so
 * the route guards would otherwise run with an empty profile and redirect the user away.
 *
 * Only checked on mount: after a login, setUser fills the profile before any route needs it.
 */
const SessionRestore = ({ children, i18n }) => {
  const dispatch = useDispatch()
  const needsRestore = useSelector(sessionSelectors.needsSessionRestore)
  const [status, setStatus] = useState(needsRestore ? STATUS.RESTORING : STATUS.DONE)

  const runRestore = useCallback(() => {
    setStatus(STATUS.RESTORING)
    Promise.resolve(dispatch(restoreSession())).then(outcome => {
      setStatus(outcome === RESTORE_OUTCOME.RETRYABLE ? STATUS.FAILED : STATUS.DONE)
    })
  }, [dispatch])

  useEffect(() => {
    if (status === STATUS.RESTORING) runRestore()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // An expired token, a DPoP error or an IP mismatch is handled inside ApiEndpoint, which
  // dispatches expireSession and never settles the restoreSession promise. expireSession
  // clears the token, so needsRestore turns false: render the routes and let the guards send
  // the user to login. Checking needsRestore (not only the local status) also means a later
  // in-app login is never held behind this loader.
  if (!needsRestore || status === STATUS.DONE) {
    return children
  }

  if (status === STATUS.FAILED) {
    return (
      <div className={styles.failed} role="alert">
        <p className={styles.message}>{i18n.notifications.canNotConnectToServer}</p>
        <button className={styles.retry} onClick={runRestore} type="button">
          {i18n.generics.tryAgainLabel}
        </button>
      </div>
    )
  }

  return <ApplicationBootstrapFallback />
}

SessionRestore.propTypes = {
  children: PropTypes.node.isRequired,
  i18n: I18N.isRequired,
}

export default Translation(SessionRestore, ['generics', 'notifications'])
